import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import api from "../api/axios";
import { useAuth } from "../context/AuthContext";
import "./Navbar.css";
import {
  LayoutDashboard, Calendar, Megaphone,
  Briefcase, Target, Package, Umbrella,
  Clock, UserCog, Users, FolderOpen,
  FileText, Shield, Wallet,
  Fingerprint, Receipt, Settings, Building2,
  LogOut, User, Menu, X, Handshake, Mail, TrendingUp, FilePlus, Activity, Bell
} from "lucide-react";

function Navbar() {
  const { user, logout, hasPermission, unreadNotificationsCount, notifications } = useAuth();
  const unreadTaskNotificationsCount = notifications?.filter(n => !n.isRead && n.type === 'Task').length || 0;
  const unreadAnnouncementNotificationsCount = notifications?.filter(n => !n.isRead && n.type === 'Announcement').length || 0;
  const unreadAssignmentNotificationsCount = notifications?.filter(n => !n.isRead && n.type === 'Project').length || 0;
  const unreadLeaveNotificationsCount = notifications?.filter(n => !n.isRead && n.type === 'Leave').length || 0;
  const unreadClientNotificationsCount = notifications?.filter(n => !n.isRead && n.type === 'Client').length || 0;
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [unviewedBroadcasts, setUnviewedBroadcasts] = useState(0);
  const isActive = (path) =>
    location.pathname === path ? "nav-item-active" : "";
  const isActiveAny = (paths) =>
    paths.some((path) => location.pathname === path) ? "nav-item-active" : "";

  const isAdmin = user?.role?.name === 'Admin';

  // Sync body class so all page containers adjust their margin
  useEffect(() => {
    if (sidebarExpanded) {
      document.body.classList.add("sidebar-expanded");
    } else {
      document.body.classList.remove("sidebar-expanded");
    }
    return () => document.body.classList.remove("sidebar-expanded");
  }, [sidebarExpanded]);

  useEffect(() => {
    fetchUnviewedCounts();
    const interval = setInterval(fetchUnviewedCounts, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchUnviewedCounts = async () => {
    try {
      const broadcastsRes = await api.get("/broadcasts/unviewed/count");
      setUnviewedBroadcasts(broadcastsRes.data.count);
    } catch (error) {
      console.error("Error fetching unviewed counts:", error);
    }
  };

  const handleLinkClick = () => {
    setIsOpen(false);
  };

  const recruitmentLinks = [
    { to: "/recruitment/dashboard", label: "Dashboard", icon: <LayoutDashboard size={15} />, activePaths: ["/recruitment/dashboard"] },
    { to: "/recruitment/jobs", label: "Jobs", icon: <Briefcase size={15} />, activePaths: ["/recruitment/jobs", "/recruitment/add-job", "/recruitment/create-job"] },
    { to: "/recruitment/job-applications", label: "Job Applications", icon: <Users size={15} />, activePaths: ["/recruitment/job-applications", "/recruitment/pipeline"] },
    { to: "/recruitment/interview-schedule", label: "Interview Schedule", icon: <Calendar size={15} />, activePaths: ["/recruitment/interview-schedule", "/recruitment/schedule-interview"] },
    { to: "/recruitment/letters", label: "Letters", icon: <FileText size={15} />, activePaths: ["/recruitment/letters"] },
    { to: "/recruitment/candidate-database", label: "Candidate Database", icon: <UserCog size={15} />, activePaths: ["/recruitment/candidate-database", "/recruitment/add-candidate"] },
  ];

  return (
    <>
      {/* Mobile Header */}
      <div className="mobile-header">
        <h2>HRMS</h2>
        <button className="hamburger" onClick={() => setIsOpen(!isOpen)}>
          {isOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <div
        className={`mobile-overlay ${isOpen ? "open" : ""}`}
        onClick={() => setIsOpen(false)}
      />

      <nav className={`navbar ${sidebarExpanded ? "expanded" : ""} ${isOpen ? "open" : ""}`}>
        <div className="navbar-container">

          {/* Logo + desktop toggle */}
          <div className="navbar-brand">
            <Link to="/" onClick={handleLinkClick} className="brand-link">
              <div
                className="brand-text-icon"
                onClick={(e) => { e.preventDefault(); if (!sidebarExpanded) setSidebarExpanded(true); }}
                title={!sidebarExpanded ? "Expand sidebar" : undefined}
                style={!sidebarExpanded ? { cursor: 'pointer' } : {}}
              >
                <img src="/logo.png" alt="Talent Recruiter" className="brand-logo-image" />
              </div>
              <span className="brand-text-full">
                <span className="brand-text-main">Talent</span>
                <span className="brand-text-sub">Recruiter</span>
              </span>
            </Link>
            <button
              className="sidebar-toggle"
              onClick={() => setSidebarExpanded((e) => !e)}
              title="Collapse sidebar"
            >
              <X size={16} />
            </button>
          </div>

          {/* Nav Items */}
          <div className="navbar-menu">

            <Link to="/" onClick={handleLinkClick} className={`nav-item ${isActive("/")}`} title="Dashboard">
              <LayoutDashboard size={20} /><span className="nav-label">Dashboard</span>
            </Link>



            {hasPermission('calendar', 'read') && (
              <Link to="/calendar" onClick={handleLinkClick} className={`nav-item ${isActive("/calendar")}`} title="Calendar">
                <Calendar size={20} /><span className="nav-label">Calendar</span>
              </Link>
            )}

            {hasPermission('broadcasts', 'read') && (
              <Link to="/broadcasts" onClick={handleLinkClick} className={`nav-item ${isActive("/broadcasts")}`} title="Broadcasts">
                <Megaphone size={20} />
                {unreadAnnouncementNotificationsCount > 0 && <span className="nav-badge">{unreadAnnouncementNotificationsCount}</span>}
                <span className="nav-label">Announcement</span>
              </Link>
            )}

            {/* {hasPermission('assignments', 'read') && (
              <Link to="/assignments" onClick={handleLinkClick} className={`nav-item ${isActive("/assignments")}`} title="Assignments">
                <Briefcase size={20} />
                {unreadAssignmentNotificationsCount > 0 && <span className="nav-badge">{unreadAssignmentNotificationsCount}</span>}
                <span className="nav-label">Assignments</span>
              </Link>
            )} */}

            {hasPermission('clients', 'read') && (
              <Link to="/clients" onClick={handleLinkClick} className={`nav-item ${isActive("/clients")}`} title="Clients">
                <Handshake size={20} />
                {unreadClientNotificationsCount > 0 && <span className="nav-badge">{unreadClientNotificationsCount}</span>}
                <span className="nav-label">Clients</span>
              </Link>
            )}

            {/* {hasPermission('projects', 'read') && (
              <Link to="/projects" onClick={handleLinkClick} className={`nav-item ${isActive("/projects")}`} title="Projects">
                <Package size={20} /><span className="nav-label">Projects</span>
              </Link>
            )} */}

            {/* {hasPermission('deals', 'read') && (
              <Link to="/deals" onClick={handleLinkClick} className={`nav-item ${isActive("/deals")}`} title="Deals">
                <TrendingUp size={20} /><span className="nav-label">Deals</span>
              </Link>
            )} */}

            {hasPermission('accounts', 'read') && (
              <Link to="/client-payments" onClick={handleLinkClick} className={`nav-item ${isActive("/client-payments")}`} title="Client Payments">
                <Wallet size={20} /><span className="nav-label">Accounts</span>
              </Link>
            )}

            {hasPermission('proposals', 'read') && (
              <Link to="/proposals" onClick={handleLinkClick} className={`nav-item ${isActive("/proposals")}`} title="Proposals">
                <FileText size={20} /><span className="nav-label">Proposals</span>
              </Link>
            )}

            {/* {hasPermission('letters', 'read') && (
              <Link to="/letters" onClick={handleLinkClick} className={`nav-item ${isActive("/letters")}`} title="Letters">
                <Mail size={20} /><span className="nav-label">Letters</span>
              </Link>
            )} */}

            {hasPermission('goals', 'read') && (
              <Link to="/goals" onClick={handleLinkClick} className={`nav-item ${isActive("/goals")}`} title="Goals">
                <Target size={20} />
                {unreadTaskNotificationsCount > 0 && <span className="nav-badge">{unreadTaskNotificationsCount}</span>}
                <span className="nav-label">Tasks</span>
              </Link>
            )}

            {hasPermission('assets', 'read') && (
              <Link to="/assets" onClick={handleLinkClick} className={`nav-item ${isActive("/assets")}`} title="Assets">
                <Package size={20} /><span className="nav-label">Assets</span>
              </Link>
            )}

            {hasPermission('leaves', 'read') && (
              <Link to="/leaves" onClick={handleLinkClick} className={`nav-item ${isActive("/leaves")}`} title="Leaves">
                <Umbrella size={20} />
                {unreadLeaveNotificationsCount > 0 && <span className="nav-badge">{unreadLeaveNotificationsCount}</span>}
                <span className="nav-label">Leaves</span>
              </Link>
            )}

            {hasPermission('attendance', 'read') && (
              <Link to="/attendance" onClick={handleLinkClick} className={`nav-item ${isActive("/attendance")}`} title="Attendance">
                <Clock size={20} /><span className="nav-label">Attendance</span>
              </Link>
            )}

            {hasPermission('activities', 'read') && (
              <Link to="/activities" onClick={handleLinkClick} className={`nav-item ${isActive("/activities")}`} title="Activities">
                <Activity size={20} /><span className="nav-label">Activities</span>
              </Link>
            )}

            {(hasPermission('leads', 'read') || hasPermission('leads', 'create')) && (
              <Link to="/leads" onClick={handleLinkClick} className={`nav-item ${isActive("/leads")}`} title="My Leads">
                <UserCog size={20} /><span className="nav-label">My Leads</span>
              </Link>
            )}

            {hasPermission('employees', 'read') && (
              <Link to="/employees" onClick={handleLinkClick} className={`nav-item ${isActive("/employees")}`} title="Employees">
                <Users size={20} /><span className="nav-label">Employees</span>
              </Link>
            )}

            {hasPermission('documents', 'read') && (
              <Link to="/documents" onClick={handleLinkClick} className={`nav-item ${isActive("/documents")}`} title="Documents">
                <FolderOpen size={20} /><span className="nav-label">Documents</span>
              </Link>
            )}

            {hasPermission('my-documents', 'read') && (
              <Link to="/my-documents" onClick={handleLinkClick} className={`nav-item ${isActive("/my-documents")}`} title="My Documents">
                <FileText size={20} /><span className="nav-label">My Documents</span>
              </Link>
            )}

            {hasPermission('roles', 'read') && (
              <Link to="/roles" onClick={handleLinkClick} className={`nav-item ${isActive("/roles")}`} title="Roles">
                <Shield size={20} /><span className="nav-label">User & Roles</span>
              </Link>
            )}

            {hasPermission("payroll", "read") && (
              <Link to="/payroll" onClick={handleLinkClick} className={`nav-item ${isActive("/payroll")}`} title="Payroll">
                <Wallet size={20} /><span className="nav-label">Payroll</span>
              </Link>
            )}

            {/* {hasPermission("biometric-logs", "read") && (
              <Link to="/biometric-attendance" onClick={handleLinkClick} className={`nav-item ${isActive("/biometric-attendance")}`} title="Biometric">
                <Fingerprint size={20} /><span className="nav-label">Biometric Logs</span>
              </Link>
            )} */}

            {hasPermission("my-payslips", "read") && (
              <Link to="/my-payroll" onClick={handleLinkClick} className={`nav-item ${isActive("/my-payroll")}`} title="My Payslip">
                <Receipt size={20} /><span className="nav-label">My Payslip</span>
              </Link>
            )}

            {isAdmin && (
              <Link to="/settings" onClick={handleLinkClick} className={`nav-item ${isActive("/settings")}`} title="Settings">
                <Settings size={20} /><span className="nav-label">Settings</span>
              </Link>
            )}

            {/* Recruitment Section */}
            {hasPermission('recruitment', 'read') && (
              <div className="recruit-section">
                <div className="recruit-section-label" title="Recruit">
                  <Building2 size={15} />
                  <span className="nav-label">Recruit</span>
                </div>
                {recruitmentLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={handleLinkClick}
                    className={`nav-item recruit-nav-item ${isActiveAny(link.activePaths)}`}
                    title={link.label}
                  >
                    {link.icon}
                    <span className="nav-label">{link.label}</span>
                  </Link>
                ))}
              </div>
            )}

            <Link to="/notifications" onClick={handleLinkClick} className={`nav-item ${isActive("/notifications")}`} title="Notifications">
              <Bell size={20} />
              {unreadNotificationsCount > 0 && <span className="nav-badge">{unreadNotificationsCount}</span>}
              <span className="nav-label">Notifications</span>
            </Link>
          </div>

          {/* User / Logout */}
          <div className="navbar-user">
            <Link to="/profile" onClick={handleLinkClick} className="nav-item nav-user-avatar" title={`${user?.firstName || ""} ${user?.lastName || ""}`}>
              <User size={20} />
              <span className="nav-label">{user?.firstName} {user?.lastName}</span>
            </Link>
            <div className="nav-role-badge">{user?.role?.name}</div>
            <button onClick={() => { logout(); setIsOpen(false); }} className="nav-item nav-logout" title="Logout">
              <LogOut size={20} />
              <span className="nav-label">Logout</span>
            </button>
          </div>

        </div>
      </nav>
    </>
  );
}

export default Navbar;
